import React, { useCallback, useMemo } from 'react'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import { button } from '@groovehq/internal-design-system/lib/styles/elements'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { CHANNEL_TYPES, drawerConfig } from '../Channels.data'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const AUTH_ERRORS = {
  ERR_CREATE_MAILBOX: `There was an error creating the ${app.t(
    'mailbox'
  )} in your account.`,
  ERR_CREATE_WIDGET:
    'There was an error creating the chat widget in your account.',
  ERR_USER_CLOSED_POPUP:
    'Oh no! Looks like you closed the authentication pop up. Please try again.',
  ALREADY_AUTHORIZED: (
    <>
      The groove app has already been authorized on gmail. Please remove it{' '}
      <a
        css={button.styles.link}
        target="_blank"
        rel="noopener noreferrer"
        href="https://security.google.com/settings/security/permissions"
      >
        here
      </a>{' '}
      before continuing.
    </>
  ),
  ALREADY_LINKED: `The address is already linked to a different ${app.t(
    'mailbox'
  )} in your groove account.`,
  ERR_FETCH_OAUTH_URL:
    'An unknown error occurred. This could be due to network connectivity or an error from the authentication service.',
  DEFAULT:
    'An unknown error occurred. This could be due to network connectivity or an error from the authentication service.',
}

const AuthenticateFail = ({
  onClose,
  onExit,
  drawerChannelType: channelType,
  previousDrawer,
  drawerAuthErrorCode: authErrorCode,
}) => {
  const channel = CHANNEL_TYPES[channelType] || {}
  const { name } = channel

  const handleCloseWithRetryOption = useCallback(
    shouldRetry => {
      const closeParams = {
        ignoreStack: shouldRetry !== 1,
      }
      if (previousDrawer?.drawerId) {
        closeParams.query = {
          ...buildDrawerQueryParam(
            previousDrawer.drawerId,
            'drawerRetryAuth',
            shouldRetry
          ),
        }
      }
      onClose(closeParams)
    },
    [onClose, previousDrawer?.drawerId]
  )

  const handleClose = useCallback(
    () => {
      handleCloseWithRetryOption(0)
    },
    [handleCloseWithRetryOption]
  )

  const handleRetry = useCallback(
    () => {
      handleCloseWithRetryOption(1)
    },
    [handleCloseWithRetryOption]
  )

  const footer = useMemo(
    () => {
      return (
        <ModalBtns tertiaryBtnText="Close" onClickTertiaryBtn={handleClose} />
      )
    },
    [handleClose]
  )

  return (
    <>
      <Drawer title={DEFAULT_DRAWER_TITLE} onClose={onExit} footer={footer}>
        <HeaderWithImage
          className="grui mt-14 mb-12"
          title="Authenticate failed"
          intro={`There was an error authenticating ${name}. Please try again.`}
        />
        <MessageCard type="negative">
          {AUTH_ERRORS[authErrorCode] || AUTH_ERRORS.DEFAULT}
        </MessageCard>
        <button
          type="button"
          className="grui mt-12"
          css={[button.styles.secondary, button.sizes.big]}
          onClick={handleRetry}
        >
          Retry authentication
        </button>
      </Drawer>
    </>
  )
}

export default AuthenticateFail
